import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { injectIntl, defineMessages } from 'react-intl';
import Dropdown from '../../components/Dropdown';
import ManageUsersModal from './manage-users-modal';
import './styles.css';

const messages = defineMessages({
  actions: {
    id: 'manage.users.dropdown.actions',
    defaultMessage: 'Actions',
  },
  edit: {
    id: 'manage.users.dropdown.edit',
    defaultMessage: 'Edit',
  },
  unlock: {
    id: 'manage.users.dropdown.unlock',
    defaultMessage: 'Unlock',
  },
  unlockHeader: {
    id: 'manage.users.dropdown.unlockHeader',
    defaultMessage: 'Unlock this user',
  },
  unlockConfirmation: {
    id: 'manage.users.dropdown.unlockConfirmation',
    defaultMessage: 'Do you want to unlock this user?',
  },
  reInviteUser: {
    id: 'manage.users.dropdown.reInviteUser',
    defaultMessage: 'Re-invite',
  },
  reInviteUserHeader: {
    id: 'manage.users.dropdown.reInviteUserHeader',
    defaultMessage: 'Re-invite this user',
  },
  reInviteUserConfirmation: {
    id: 'manage.users.dropdown.reInviteUserConfirmation',
    defaultMessage: 'Do you want to re-invite this user?',
  },
  activate: {
    id: 'manage.users.dropdown.activate',
    defaultMessage: 'Activate',
  },
  activateChangeHeader: {
    id: 'manage.users.dropdown.activate-header',
    defaultMessage: 'Activate this user',
  },
  activateConfirmation: {
    id: 'manage.users.dropdown.activateConfirmation',
    defaultMessage: 'Do you want to activate this user?',
  },
  inactivate: {
    id: 'manage.users.dropdown.inactivate',
    defaultMessage: 'Inactivate',
  },
  inactivateChangeHeader: {
    id: 'manage.users.dropdown.inactivate-header',
    defaultMessage: 'Inactivate this user',
  },
  inactivateConfirmation: {
    id: 'manage.users.dropdown.inactivateConfirmation',
    defaultMessage: 'Do you want to inactivate this user?',
  },
});

class ManageUsersActionDropdown extends React.PureComponent {
  static propTypes = {
    user: PropTypes.object.isRequired,
    showReInviteUser: PropTypes.bool,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    onStatusChange: PropTypes.func,
    onUnlock: PropTypes.func,
    onReInviteUser: PropTypes.func,
  };

  static contextTypes = {
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      reInviteUserConfirmOpen: false,
      statusChangeConfirmOpen: false,
      unlockConfirmOpen: false,
    };
  }

  onChange = (e, { value }) => {
    if (value === 'edit') {
      this.context.router.push(this.getActionUrl(value));
    }
    if (value === 'activate' || value === 'inactivate') {
      this.setState({ statusChangeConfirmOpen: true });
    }
    if (value === 'unlock') {
      this.setState({ unlockConfirmOpen: true });
    }
    if (value === 're-invite') {
      this.setState({ reInviteUserConfirmOpen: true });
    }
  };

  getActionUrl(value) {
    const actionUrls = {
      edit: `/manageUsers/edit-user/${this.props.user.id}`,
    };
    return actionUrls[value];
  }

  handleStatusChangeConfirm = (e, data) => {
    this.setState({ changingStatus: true, errorDetails: '' });
    this.props.onStatusChange(this.props.user).then(() => {
      this.setState({ statusChangeConfirmOpen: false, changingStatus: false, errorDetails: '' });
    }).catch((error) => {
      this.setState({ changingStatus: false, errorDetails: 'Could not change status of user' });
    });
  };

  handleStatusChangeCancel = (e, data) => {
    this.setState({ statusChangeConfirmOpen: false, errorDetails: '' });
  };

  handleUnlockConfirm = (e, data) => {
    this.setState({ unlockConfirmOpen: false, unlocking: true });
    this.props.onUnlock(this.props.user).then(() => {
      this.setState({ unlocking: false, errorDetails: '' });
    }).catch((error) => {
      this.setState({ unlocking: false, errorDetails: 'Could not unlock user' });
    });
  };

  handleReInviteUserConfirm = (e, data) => {
    this.setState({ reInviteUserConfirmOpen: false, reInviting: true });
    this.props.onReInviteUser(this.props.user).then(() => {
      this.setState({ reInviting: false, errorDetails: '' });
    }).catch((error) => {
      this.setState({ reInviting: false, errorDetails: 'Could not re-invite user' });
    });
  };

  handleReInviteUserCancel = (e, data) => {
    this.setState({ reInviteUserConfirmOpen: false, errorDetails: '' });
  };

  handleUnlockCancel = (e, data) => {
    this.setState({ unlockConfirmOpen: false, errorDetails: '' });
  };

  createDropdownOptions = (user) => {
    const { intl, showReInviteUser } = this.props;
    const lockStatus = JSON.parse(user.lockStatus);
    const userIsLocked = lockStatus && lockStatus.locked;
    const statusMessage = user.active ? messages.inactivate : messages.activate;
    const ddOptions = [
      {
        text: intl.formatMessage(messages.edit),
        value: 'edit',
      },
      {
        text: <span styleName={!userIsLocked ? 'dropdown-disabled-item' : ''}>{intl.formatMessage(messages.unlock)}</span>,
        value: 'unlock',
        disabled: !userIsLocked,
      },
      {
        'text': intl.formatMessage(statusMessage),
        'value': statusMessage.defaultMessage.toLowerCase(),
        'data-cy': `user-actions-menu-${statusMessage.defaultMessage.toLowerCase()}`,
      },
    ];
    if (showReInviteUser) {
      ddOptions.push({
        text: intl.formatMessage(messages.reInviteUser),
        value: 're-invite',
      });
    }
    return ddOptions;
  };

  render() {
    const { user, intl } = this.props;
    const { statusChangeConfirmOpen,
      changingStatus,
      errorDetails,
      unlockConfirmOpen,
      unlocking,
      reInviteUserConfirmOpen,
      reInviting } = this.state;
    let statusChangeHeader = '';
    let statusChangeConfirmation = '';
    const unlockHeader = intl.formatMessage(messages.unlockHeader);
    const unlockConfirmation = intl.formatMessage(messages.unlockConfirmation);
    const reInviteUserHeader = intl.formatMessage(messages.reInviteUserHeader);
    const reInviteUserConfirmation = intl.formatMessage(messages.reInviteUserConfirmation);
    if (user.active) {
      statusChangeHeader = intl.formatMessage(messages.inactivateChangeHeader);
      statusChangeConfirmation = intl.formatMessage(messages.inactivateConfirmation);
    } else {
      statusChangeHeader = intl.formatMessage(messages.activateChangeHeader);
      statusChangeConfirmation = intl.formatMessage(messages.activateConfirmation);
    }
    const dropdownOptions = this.createDropdownOptions(user);
    return (
      <div>
        <Dropdown
          value="none"
          selectOnBlur={false}
          selectOnNavigation={false}
          onChange={this.onChange}
          className="selection"
          floating
          options={dropdownOptions}
          aria-label={intl.formatMessage(messages.actions)}
          placeholder="Actions"
          trigger={(<span data-cy="user-actions-menu">{intl.formatMessage(messages.actions)}</span>)}
          styleName="dropdown-placeholder"
        />
        <ManageUsersModal
          user={user}
          changing={unlocking}
          confirmOpen={unlockConfirmOpen}
          confirmMessage={unlockConfirmation}
          header={unlockHeader}
          onCancel={this.handleUnlockCancel}
          onConfirm={this.handleUnlockConfirm}
          id="manage-user-unlock-modal"
          errorDetails={errorDetails}
        />
        <ManageUsersModal
          user={user}
          changing={changingStatus}
          confirmOpen={statusChangeConfirmOpen}
          confirmMessage={statusChangeConfirmation}
          header={statusChangeHeader}
          onCancel={this.handleStatusChangeCancel}
          onConfirm={this.handleStatusChangeConfirm}
          id="manage-user-status-change-modal"
          errorDetails={errorDetails}
        />
        <ManageUsersModal
          user={user}
          changing={reInviting}
          confirmOpen={reInviteUserConfirmOpen}
          confirmMessage={reInviteUserConfirmation}
          header={reInviteUserHeader}
          onCancel={this.handleReInviteUserCancel}
          onConfirm={this.handleReInviteUserConfirm}
          id="manage-user-re-invite-user-modal"
          errorDetails={errorDetails}
        />
      </div>
    );
  }
}

export const ManageUsersActionDropdownComponent = injectIntl(ManageUsersActionDropdown);

export default connect()(ManageUsersActionDropdownComponent);
